import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { ActivityBookingData } from '@website/services/activities.service';
import { analyzeSlotsForBooking } from '@website/utils/activityBooking';

export interface ActivityPageActivityDataWithSlots {
  name: string;
  providerName: string;
  rating: string;
  reviewCount: number;
  imageSrc?: string;
  locationType: string;
  locationAddress?: string;
  /** IANA venue timezone, threaded through so the booking flow can resolve venue wall-clock to an absolute instant for browser-time display. */
  timezone?: string;
  /** The pro's conditions/disclaimer for this activity (localized). */
  disclaimer?: string;
  startingPrice: string;
  singleSlot: {
    minDuration: string;
    minPrice: number;
  } | null;
  groupSlot: {
    minDuration: string;
    minNumberOfPeople: number;
    minPrice: number;
  } | null;
  /** Open-range slots — minPrice is the smallest bookable duration's flat per-duration price. */
  rangeSlot?: {
    minDuration: string;
    minPrice: number;
  } | null;
}

export const useSlotAnalysis = (activityData: ActivityBookingData) => {
  const t = useTranslations();

  // Analyze slots to extract proper booking data
  const slotAnalysis = useMemo(() => analyzeSlotsForBooking(activityData.slots), [activityData.slots]);

  // Convert ActivityBookingData type to ActivityPageActivityDataWithSlots type
  const transformedActivity: ActivityPageActivityDataWithSlots = useMemo(
    () => ({
      name: activityData.activityName || activityData.title,
      providerName: activityData.professionalName,
      rating: '4.8', // TODO: Add rating to ActivityBookingData type when available
      reviewCount: 256, // TODO: Add reviewCount to ActivityBookingData type when available
      imageSrc: activityData.activityImage || activityData.imageUrl,
      locationType: activityData.isAtHome ? t('Booking.slots.atHome') : t('Booking.slots.atVenue'),
      locationAddress: activityData.location?.address,
      disclaimer: activityData.disclaimer,
      timezone: activityData.timezone,
      startingPrice:
        slotAnalysis.singleSlot?.minPrice?.toString() ||
        slotAnalysis.groupSlot?.minPrice?.toString() ||
        slotAnalysis.rangeSlot?.minPrice?.toString() ||
        activityData.startingPrice?.toString() ||
        '0',
      singleSlot: slotAnalysis.singleSlot,
      groupSlot: slotAnalysis.groupSlot,
      rangeSlot: slotAnalysis.rangeSlot
    }),
    [
      activityData.activityName,
      activityData.title,
      activityData.professionalName,
      activityData.activityImage,
      activityData.imageUrl,
      activityData.disclaimer,
      activityData.isAtHome,
      activityData.location?.address,
      activityData.timezone,
      activityData.startingPrice,
      slotAnalysis,
      t
    ]
  );

  // Determine initial slot type based on available slots
  const getInitialBookingType = useMemo((): 'SINGLE' | 'GROUP' | 'RANGE' => {
    // Prioritize single slot if available
    if (slotAnalysis.singleSlot) return 'SINGLE';
    // Fall back to group slot if available
    if (slotAnalysis.groupSlot) return 'GROUP';
    if (slotAnalysis.rangeSlot) return 'RANGE';
    // Default to 'SINGLE' as fallback (UI will handle disabling when no slots available)
    return 'SINGLE';
  }, [slotAnalysis.singleSlot, slotAnalysis.groupSlot, slotAnalysis.rangeSlot]);

  // Check if there's only one slot type available (skip slot selection step)
  const hasOnlyOneBookingType = useMemo(() => {
    const kinds = [slotAnalysis.singleSlot, slotAnalysis.groupSlot, slotAnalysis.rangeSlot].filter(Boolean).length;
    return kinds === 1;
  }, [slotAnalysis.singleSlot, slotAnalysis.groupSlot, slotAnalysis.rangeSlot]);

  return {
    slotAnalysis,
    transformedActivity,
    getInitialBookingType,
    hasOnlyOneBookingType
  };
};
